import React, { useEffect, useMemo, useState } from 'react';
import { StanButton, StanTable, StanTag } from '@/components';
import type { StanColumnDef } from '@/components/core-components/StanTable/StanTable.types';
import type { DisplayLoginSession } from '../../lib/security.types';
import { useLoginSessions, useLogoutAllSessions, useLogoutSession } from '@/queries';
import { stanNotify } from '@/lib/stanNotify';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { StanDropdownMenu } from '@/components/core-components/StanDropdown/Menu/StanDropdownMenu';
import type { DropdownMenuOption } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { LuEllipsisVertical } from 'react-icons/lu';
import IcLogout from '@/assets/icons/ic-log-out.svg';

// Responsive page size: mobile shows 2, desktop shows 4
const getItemsPerPage = () => {
    return window.innerWidth < 768 ? 2 : 4;
};

const ITEMS_PER_PAGE = getItemsPerPage();

const TERMINATE_OPTIONS: DropdownMenuOption[] = [
    {
        label: 'Terminate',
        value: 'terminate',
        icon: IcLogout,
    },
];

const createSessionColumns = (onLogoutSession: (sessionId: string) => void): StanColumnDef<DisplayLoginSession>[] => [
    {
        accessorKey: 'location',
        header: 'Location',
        cell: ({ row }) => <div className='break-words whitespace-normal'>{row.original.location}</div>,
    },
    {
        accessorKey: 'device',
        header: 'Device',
        cell: ({ row }) => <div className='break-words whitespace-normal'>{row.original.device}</div>,
    },
    {
        accessorKey: 'ipAddress',
        header: 'IP Address',
        cell: ({ row }) => <div className='break-words whitespace-normal'>{row.original.ipAddress}</div>,
    },
    {
        accessorKey: 'loginTime',
        header: 'Login Time',
        cell: ({ row }) => <div className='break-words whitespace-normal'>{row.original.loginTime}</div>,
    },
    {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
            <div className='flex items-center justify-end gap-2'>
                {row.original.current && (
                    <StanTag label='Current Session' className='bg-brand-subtle text-brand-main font-bold' />
                )}
                {!row.original.current && (
                    <DropdownProvider dropdownId={`session-menu-${row.original.id}`}>
                        <StanDropdownMenu
                            id={`session-menu-${row.original.id}`}
                            name={`session-menu-${row.original.id}`}
                            options={TERMINATE_OPTIONS}
                            onSelect={() => onLogoutSession(row.original.id)}
                            placement='bottom-end'
                            minWidth={140}
                            rounded
                        >
                            <button className='text-gray hover:text-dark flex size-8 cursor-pointer items-center justify-center rounded-md transition hover:bg-neutral-100'>
                                <LuEllipsisVertical className='size-5' />
                            </button>
                        </StanDropdownMenu>
                    </DropdownProvider>
                )}
            </div>
        ),
        align: 'right',
    },
];

export const LoginSessionsSection: React.FC = () => {
    const { loginSessions, isLoading } = useLoginSessions();
    const { mutate: logoutSessionMutation, isPending: isLogoutSessionPending } = useLogoutSession();
    const { mutate: logoutAllSessionsMutation, isPending: isLogoutAllSessionsPending } = useLogoutAllSessions();

    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = useMemo(() => Math.ceil(loginSessions.length / ITEMS_PER_PAGE), [loginSessions.length]);

    // Adjust current page if it exceeds total pages after sessions are removed
    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const paginatedSessions = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return loginSessions.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [loginSessions, currentPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleLogoutSession = (sessionId: string) => {
        logoutSessionMutation(sessionId);
    };

    const handleLogoutAllSessions = () => {
        logoutAllSessionsMutation(undefined, {
            onSuccess: () => {
                stanNotify({
                    type: 'success',
                    title: 'Success!',
                    text: 'All other sessions have been logged out.',
                });
            },
        });
    };

    const sessionColumns = createSessionColumns(handleLogoutSession);

    return (
        <div className='mt-8'>
            <div className='mb-4 flex flex-col items-start gap-4 md:flex-row md:justify-between'>
                <div>
                    <h4>Login Sessions</h4>
                    <p className='text-caption-1-regular mt-2'>
                        Places where you&apos;re logged into Stan. If you do not recognize a session, you can terminate
                        it below.
                    </p>
                </div>
                {loginSessions.length > 0 && (
                    <StanButton
                        size='sm'
                        name='logout-all-sessions'
                        variant='outline'
                        onClick={handleLogoutAllSessions}
                        loading={isLogoutAllSessionsPending}
                    >
                        Sign out all other sessions
                    </StanButton>
                )}
            </div>
            <StanTable<DisplayLoginSession>
                data={paginatedSessions}
                columns={sessionColumns}
                isLoading={isLoading || isLogoutSessionPending}
                emptyHeader='No Active Sessions'
                emptySubheader='You have no active login sessions.'
                className='text-caption-1-regular'
                page={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />
        </div>
    );
};
